import { motion } from "framer-motion";
import { Zap, Heart, Users, Sparkles, Download } from "lucide-react";
import Seo from "../components/Seo";

const LEADERS = [
  {
    slug: "demetrial-hill",
    name: "Pastor Demetrial Donshae Hill",
    role: "Senior Pastor",
    image: "/images/pastor-hill.jpg",
    scriptureRef: "His Heart",
    scripture: "Genuine love for God and a heartfelt desire to see people healed, delivered, and drawn closer to Christ.",
    bio: [
      "Pastor Demetrial Donshae Hill — affectionately known to many as \"Donshae\" — is a devoted man of God whose life has been rooted in faith from an early age. Raised in Chesapeake and Portsmouth, Virginia, he was brought up in church, where his love for ministry began to grow. Over the years, he became widely known for his powerful presence in the choir and quartet scene. Music was one of the first ways his gift made room for him, and it continues to be a meaningful part of his journey.",
      "A proud graduate of Great Bridge High School, Pastor Hill has always carried his upbringing and community close to his heart. He is the beloved son of Shelia Hill and Danny Hill Sr., whose foundation helped shape his character, faith, and passion for serving others.",
      "On May 5, 2021, Pastor Hill answered the call to preach the Gospel, stepping boldly into his purpose. Since that day, he has remained committed to sharing the Word of God with authenticity, compassion, and power. His ministry is marked by a genuine love for God and a heartfelt desire to see people healed, delivered, and drawn closer to Christ.",
      "Pastor Hill is also a devoted husband to his wife, First Lady Deja Hill, and together they serve with unity, grace, and a shared passion for God's people. Beyond the pulpit, he is a dedicated and loving father to his two children, Skylar Monet and Seth Jacob, who are among his greatest joys and motivations.",
      "Pastor Hill's life and leadership reflect a deep commitment to God's people. With a shepherd's heart, he continues to pour into lives, uplift communities, and inspire others to walk boldly in their faith.",
    ],
  },
  {
    slug: "deja-hill",
    name: "First Lady Déja Hill",
    role: "First Lady",
    image: "/images/first-lady-hill.jpg",
    scriptureRef: "Isaiah 61:3",
    scripture: "He will give beauty for ashes, the oil of joy for mourning, and the garment of praise for the spirit of heaviness.",
    bio: [
      "First Lady Déja Hill is a devoted wife, mother, daughter, and woman who deeply loves God. She has been faithfully married for 12 years to her husband, Pastor Demetrial Donshae Hill, standing beside him in life and ministry with grace, strength, and unwavering faith. Together, they serve as the pastoral leaders of The Push Center in Portsmouth, Virginia, where Déja serves as First Lady with authenticity, humility, and a genuine heart for people.",
      "A native of Chesapeake, Virginia, Déja attended Oscar Smith and is the youngest of four children. Her love for God is the foundation of her life and is evident in how she loves, serves, and shows up for others. She is the proud mommy of Skylar Monet and Seth Jacob, whom she nurtures with intention, prayer, and unconditional love.",
      "First Lady Déja Hill is a true girls' girl, known for her warmth and ability to make others feel seen. Through her gifts in hair and makeup, she impacts every person who sits in her chair, reminding them of their God-given beauty. She is creative, fashion-forward, and lovingly referred to by her husband as his beautiful Black Barbie — a reflection of her love for style and the fact that she dresses him too, even if he will not admit it.",
      "In addition to her creative talents, Déja has a gift of singing, one her husband encourages her to use as often as possible. Above all, she is a lover of all people, leading with humility, faith, and a heart fully committed to glorifying God.",
    ],
  },
  {
    slug: "danny-hill",
    name: "Youth Pastor Danny Hill Jr.",
    role: "Youth Pastor",
    image: "/images/youth-pastor-danny.jpg",
    scriptureRef: "Romans 8:18",
    scripture: "For I reckon that the sufferings of this present time are not worthy to be compared with the glory which shall be revealed in us.",
    bio: [
      "Danny Hill Jr. is a devoted Youth Pastor whose life is rooted in a deep and unwavering love for God. Having grown up in church, his foundation in the Word was established early — but over time it has become more than just what he was taught. It is who he is. His passion for God is evident in the way he lives, serves, and pours into others with sincerity and purpose.",
      "A proud graduate of I.C. Norcom High School, Danny is also the proud son of Shelia and Danny Hill Sr., whose love and guidance helped shape the man he is today.",
      "Danny holds a Bachelor's Degree in Human Services from Old Dominion University and a Master's Degree in Educational Studies with a concentration in Leadership and Urban Education from Liberty University. His educational background reflects his deep commitment to serving and uplifting people, especially the next generation.",
      "At the heart of Danny's ministry is his genuine love for youth and young adults. He doesn't just lead them — he walks with them, understands them, and meets them where they are. He carries a burden to see their lives transformed, their purpose discovered, and their souls connected to God. Whether through mentorship, encouragement, or simply being present, Danny is committed to making sure every young person he encounters knows they are seen, valued, and loved.",
      "He enjoys music, good food, and building real relationships — but above all, his greatest passion is reaching and nurturing the next generation for Christ.",
    ],
  },
];

const affirmations2026 = [
  "I walk boldly in God's promises.",
  "Divine miracles surround me.",
  "Breakthrough is mine.",
  "Provision and favor follow me.",
  "God is doing a new thing.",
];

export default function About() {
  return (
    <main id="main-content" className="bg-[#050505] text-white" data-testid="about-page">
      <Seo
        title="About"
        description="Meet The Push Center — our mission, vision, leadership (Pastor Demetrial Donshae Hill & First Lady Deja Hill), and what we stand for."
        path="/about"
      />
      {/* HERO */}
      <section className="relative pt-20 pb-16 lg:pt-32 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/congregation.jpg" alt="" className="w-full h-full object-cover opacity-70 bg-zoom" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/20" />
          <div className="absolute inset-0 lightning-grid opacity-25" />
        </div>
        <div className="absolute top-20 right-0 w-96 h-96 bg-electric/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-5">
            About The Push Center
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-4xl">
            We don't just <span className="italic text-electric text-pulse">gather.</span><br />
            We build <span className="italic text-electric text-pulse">lives.</span>
          </h1>
          <p className="mt-8 text-lg lg:text-xl text-zinc-300 max-w-3xl leading-relaxed">
            Welcome home. The Push Center exists to push you Out of Complacency Into Your Destiny —
            the destiny God has prepared for you. It's not a coincidence you found us — it's divine alignment.
          </p>
        </div>
      </section>

      {/* MISSION / VISION */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-24 grid lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative p-10 lg:p-12 rounded-3xl bg-[#0c0c0c] border border-white/5 overflow-hidden"
          data-testid="mission-card"
        >
          <div className="absolute -top-8 -right-8 w-40 h-40 bg-electric/10 rounded-full blur-2xl" />
          <Zap className="w-10 h-10 text-electric mb-6" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-electric mb-3">Our Mission</div>
          <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tight mb-5">
            Build people. Strengthen faith.
          </h2>
          <p className="text-zinc-300 leading-relaxed mb-3">
            To build a ministry where people don't just attend — but belong. We reach every soul,
            regardless of background, and create genuine, lasting connections in our community.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="relative p-10 lg:p-12 rounded-3xl bg-[#0c0c0c] border border-white/5 overflow-hidden"
          data-testid="vision-card"
        >
          <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-electric/10 rounded-full blur-2xl" />
          <Sparkles className="w-10 h-10 text-electric mb-6" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-electric mb-3">Our Vision</div>
          <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tight mb-5">
            A safe space to grow, heal & thrive.
          </h2>
          <p className="text-zinc-300 leading-relaxed">
            To establish an environment where individuals and families can grow spiritually, heal
            emotionally, and thrive in purpose — cultivating authentic relationships where everyone
            feels seen, valued, and connected.
          </p>
        </motion.div>
      </section>

      {/* OUR STORY */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 lightning-grid opacity-20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-electric/5 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-5 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 lg:sticky lg:top-28">
              <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">
                Our Story
              </div>
              <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
                From <span className="italic text-electric">8 people</span> around a table.
              </h2>
              <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-electric/30 bg-electric/5">
                <Zap className="w-3.5 h-3.5 text-electric" />
                <span className="text-xs uppercase tracking-[0.25em] text-electric font-bold">Birthed May 2022</span>
              </div>
            </div>

            <div className="lg:col-span-8 space-y-6 text-zinc-300 leading-relaxed text-lg">
              <p>
                The Push Center was birthed in May 2022 with just 8 people gathered around a table,
                carrying a God-given dream that would soon become a reality. What started as a simple
                "yes" to God has grown into a ministry built on faith, sacrifice, love, and purpose.
              </p>
              <p>
                Our journey has not always been easy, but every step has been intentional. From operating
                out of two different daycare facilities — faithfully setting up before every service and
                breaking down after every worship experience — we learned that church was never about
                the building, but about the <span className="text-electric italic">people and the presence of God.</span> Through every transition,
                God remained faithful.
              </p>
              <p>
                As the ministry continued to grow, God blessed us with our own building, and now we are
                honored to serve our community from our current home on Portsmouth Blvd. Each season has
                been a testament to what God can do when people are willing to trust Him completely.
              </p>
              <p>
                At The Push Center, we believe in our creed:
                <span className="block mt-4 mb-2 font-display font-black text-2xl lg:text-3xl text-electric not-italic">
                  "Out of Complacency Into Your Destiny."
                </span>
                These words are the foundation of who we are. We are committed to pushing people beyond
                comfort, beyond fear, and beyond limitation so they can walk boldly into the purpose and
                destiny God has designed for their lives.
              </p>
              <p className="text-zinc-400">
                While we are grateful for how far God has brought us, we know our story is still being
                written. The history of The Push Center continues to unfold every single day, and we
                would love for you to join us as we continue building, growing, and impacting lives together.
              </p>
              <p className="font-display font-bold text-xl text-white pt-2">
                There is a place for you here.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="relative py-24 lg:py-32 bg-[#080808] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="max-w-3xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">
              Leadership
            </div>
            <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
              The shepherds of <span className="italic text-electric">The Push.</span>
            </h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
              Walking in obedience to the call — leading with love, integrity, and authenticity.
            </p>
          </div>

          {/* EPK Downloads — one branded press kit per leader */}
          <div className="mb-16 rounded-3xl border border-electric/30 bg-gradient-to-r from-electric/10 via-electric/5 to-transparent p-6 sm:p-8" data-testid="epk-card">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
              <div className="max-w-2xl">
                <div className="text-[10px] uppercase tracking-[0.3em] text-electric font-bold mb-2 inline-flex items-center gap-2">
                  <Sparkles className="w-3 h-3" /> Press & Media
                </div>
                <h3 className="font-display font-bold text-xl sm:text-2xl text-white tracking-tight mb-1">
                  Download An Electronic Press Kit
                </h3>
                <p className="text-sm text-zinc-400">
                  A branded PDF for each leader — bio, photo, scripture, and contact info. Perfect for podcasts, conferences, and interviews.
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {LEADERS.map((leader) => (
                <a
                  key={leader.slug}
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/epk/${leader.slug}`}
                  download
                  className="group flex items-center gap-3 p-3 rounded-2xl bg-black/40 border border-white/10 hover:border-electric hover:bg-electric/5 transition-all"
                  data-testid={`epk-download-${leader.slug}`}
                >
                  <img src={leader.image} alt="" className="w-14 h-14 rounded-xl object-cover object-top" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-electric font-bold">{leader.role}</div>
                    <div className="text-sm font-bold text-white truncate group-hover:text-electric transition-colors">{leader.name}</div>
                    <div className="text-[10px] text-zinc-500 inline-flex items-center gap-1 mt-0.5">
                      <Download className="w-2.5 h-2.5" /> EPK PDF
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {LEADERS.map((leader, idx) => (
            <motion.div
              key={leader.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${idx > 0 ? "mt-20 lg:mt-28" : ""}`}
              data-testid={`leader-${idx}`}
            >
              <div className={`lg:col-span-5 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="relative aspect-[4/5] rounded-2xl overflow-hidden group">
                  <img
                    src={leader.image}
                    alt={leader.name}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div className="absolute inset-0 ring-1 ring-electric/30 rounded-2xl" />
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-electric mb-1">{leader.role}</div>
                    <div className="font-display font-black text-3xl leading-none">{leader.name}</div>
                  </div>
                </div>
              </div>

              <div className={`lg:col-span-7 ${idx % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="text-[10px] uppercase tracking-[0.3em] text-electric mb-3">{leader.role}</div>
                <h3 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mb-6">
                  {leader.name}
                </h3>
                <div className="space-y-4 text-zinc-300 leading-relaxed">
                  {leader.bio.map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
                {leader.scripture && (
                  <div className="mt-6 pl-5 border-l-2 border-electric italic text-zinc-400 text-sm">
                    <strong className="text-electric not-italic">{leader.scriptureRef}</strong> — “{leader.scripture}”
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-24 lg:py-32">
        <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">
          What We Stand For
        </div>
        <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter mb-16 max-w-4xl">
          Core values that <span className="italic text-electric">push</span> us forward.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Heart, label: "Belonging", desc: "Everyone has a seat at the table." },
            { icon: Users, label: "Unity", desc: "Together we move farther." },
            { icon: Sparkles, label: "Transformation", desc: "Change is possible — daily." },
            { icon: Zap, label: "Generosity", desc: "Freely we've received; freely we give." },
          ].map((v, i) => (
            <motion.div
              key={v.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="p-7 rounded-2xl border border-white/5 bg-[#0a0a0a] hover:border-electric/40 transition group"
              data-testid={`value-card-${i}`}
            >
              <v.icon className="w-7 h-7 text-electric mb-5 group-hover:scale-110 transition-transform" />
              <div className="font-display font-bold text-xl mb-2">{v.label}</div>
              <div className="text-sm text-zinc-400">{v.desc}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 2026 AFFIRMATION */}
      <section className="relative py-24 bg-gradient-to-b from-[#050505] via-[#0a1a0a] to-[#050505] border-y border-electric/10">
        <div className="max-w-4xl mx-auto px-5 lg:px-10 text-center">
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-5">
            2026 Affirmation
          </div>
          <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tight mb-12">
            This is our declaration.
          </h2>
          <div className="space-y-5">
            {affirmations2026.map((a, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                className="font-display font-bold text-xl lg:text-2xl text-zinc-200"
                data-testid={`affirmation-${i}`}
              >
                <span className="text-electric mr-3">+</span>{a}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
